import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Calendar, Plus, Clock, CheckCircle2, Circle, Trash2, Pencil, X } from 'lucide-react';

function TaskModal({ initial, leads, onSave, onClose }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState({
    titulo: initial?.titulo || '',
    descripcion: initial?.descripcion || '',
    due_at: initial?.due_at ? initial.due_at.slice(0, 16) : '',
    lead_id: initial?.lead_id || '',
    completed: initial?.completed || false,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
        lead_id: form.lead_id || null,
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo guardar la tarea');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="task-modal" className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2><Calendar size={20} /> {isEdit ? 'Editar tarea' : 'Nueva tarea'}</h2>
          <button className="close-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Título *</label>
            <input data-testid="task-titulo" value={form.titulo} onChange={update('titulo')} required maxLength={255} />
          </div>
          <div className="form-group">
            <label>Descripción</label>
            <textarea data-testid="task-descripcion" rows={3} value={form.descripcion} onChange={update('descripcion')} maxLength={2000} />
          </div>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Vence el</label>
              <input data-testid="task-due" type="datetime-local" value={form.due_at} onChange={update('due_at')} />
            </div>
            <div className="form-group">
              <label>Vincular a Lead (opcional)</label>
              <select data-testid="task-lead" value={form.lead_id} onChange={update('lead_id')}>
                <option value="">Sin lead vinculado</option>
                {leads.map(l => (
                  <option key={l.id} value={l.id}>{l.nombre} {l.apellido}</option>
                ))}
              </select>
            </div>
          </div>
          {isEdit && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input data-testid="task-completed" type="checkbox" checked={form.completed} onChange={update('completed')} />
                Marcar como completada
              </label>
            </div>
          )}
          {error && <div data-testid="task-error" className="form-error">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancelar</button>
            <button data-testid="task-submit" type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : (isEdit ? 'Guardar cambios' : 'Crear tarea')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TaskRow({ task, leadsById, onToggle, onEdit, onDelete }) {
  const lead = task.lead_id ? leadsById[task.lead_id] : null;
  const dueDate = task.due_at ? new Date(task.due_at) : null;
  const overdue = dueDate && !task.completed && dueDate < new Date();

  return (
    <div className="obs-item" data-testid={`task-${task.id}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <button
        data-testid={`task-toggle-${task.id}`}
        className="btn-icon"
        onClick={() => onToggle(task)}
        title={task.completed ? 'Reabrir' : 'Marcar completada'}
      >
        {task.completed
          ? <CheckCircle2 size={20} color="#059669" />
          : <Circle size={20} color="#94a3b8" />}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, textDecoration: task.completed ? 'line-through' : 'none', opacity: task.completed ? 0.6 : 1 }}>
          {task.titulo}
        </div>
        {task.descripcion && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{task.descripcion}</div>
        )}
        <div className="obs-meta" style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {dueDate && (
            <span style={{ color: overdue ? '#dc2626' : 'var(--text-muted)' }}>
              <Clock size={12} /> {dueDate.toLocaleString('es-VE', { dateStyle: 'short', timeStyle: 'short' })}
              {overdue ? ' · Vencida' : ''}
            </span>
          )}
          {lead && <span>· Lead: {lead.nombre} {lead.apellido}</span>}
        </div>
      </div>
      <button data-testid={`task-edit-${task.id}`} className="btn-icon" onClick={() => onEdit(task)} title="Editar"><Pencil size={16} /></button>
      <button data-testid={`task-delete-${task.id}`} className="btn-icon" onClick={() => onDelete(task)} title="Eliminar"><Trash2 size={16} color="#dc2626" /></button>
    </div>
  );
}

export default function AgendaPage() {
  const [tasks, setTasks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('pending'); // pending | completed | all

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [agendaRes, leadsRes] = await Promise.all([
        api.get('/agenda'),
        api.get('/leads', { params: { limit: 200 } }),
      ]);
      setTasks(agendaRes.data || []);
      setLeads(leadsRes.data?.leads || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo cargar la agenda');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const leadsById = leads.reduce((acc, l) => ({ ...acc, [l.id]: l }), {});

  const handleSave = async (payload) => {
    if (editing?.id) {
      const { data } = await api.patch(`/agenda/${editing.id}`, payload);
      setTasks(prev => prev.map(t => t.id === data.id ? data : t));
    } else {
      const { data } = await api.post('/agenda', payload);
      setTasks(prev => [data, ...prev]);
    }
  };

  const handleToggle = async (task) => {
    try {
      const { data } = await api.patch(`/agenda/${task.id}`, { completed: !task.completed });
      setTasks(prev => prev.map(t => t.id === data.id ? data : t));
    } catch (err) {
      window.alert(err?.response?.data?.detail || 'No se pudo actualizar');
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Eliminar la tarea "${task.titulo}"?`)) return;
    try {
      await api.delete(`/agenda/${task.id}`);
      setTasks(prev => prev.filter(t => t.id !== task.id));
    } catch (err) {
      window.alert(err?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const filtered = tasks.filter(t => {
    if (filter === 'pending') return !t.completed;
    if (filter === 'completed') return t.completed;
    return true;
  });

  const pendingCount = tasks.filter(t => !t.completed).length;
  const overdueCount = tasks.filter(t => !t.completed && t.due_at && new Date(t.due_at) < new Date()).length;

  if (loading) return <div className="page-loading">Cargando agenda...</div>;

  return (
    <div data-testid="agenda-page" className="contacts-page">
      <div className="contacts-toolbar">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
          <Calendar size={18} /> Mi Agenda
          <span className="text-muted" style={{ fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            {pendingCount} pendiente{pendingCount !== 1 ? 's' : ''}
            {overdueCount > 0 && ` · ${overdueCount} vencida${overdueCount !== 1 ? 's' : ''}`}
          </span>
        </h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <select data-testid="agenda-filter" value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="pending">Pendientes</option>
            <option value="completed">Completadas</option>
            <option value="all">Todas</option>
          </select>
          <button
            data-testid="new-task-button"
            className="btn-primary"
            onClick={() => { setEditing(null); setModalOpen(true); }}
          >
            <Plus size={16} /> Nueva tarea
          </button>
        </div>
      </div>

      {error && <div className="form-error" style={{ marginTop: 12 }}>{error}</div>}

      <div className="dash-card" style={{ marginTop: 16 }}>
        {filtered.length === 0
          ? <p className="text-muted" style={{ textAlign: 'center', padding: 32 }}>
              {filter === 'pending' ? 'No tienes tareas pendientes. Aprovecha y crea una nueva.' :
               filter === 'completed' ? 'Aún no completaste ninguna tarea.' : 'Tu agenda está vacía.'}
            </p>
          : filtered.map(t => (
              <TaskRow
                key={t.id}
                task={t}
                leadsById={leadsById}
                onToggle={handleToggle}
                onEdit={(task) => { setEditing(task); setModalOpen(true); }}
                onDelete={handleDelete}
              />
            ))}
      </div>

      {modalOpen && (
        <TaskModal
          initial={editing}
          leads={leads}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditing(null); }}
        />
      )}
    </div>
  );
}
